import {_, React, observable, computed, IDisposer, action, autorun, runInAction, stringify} from '@api'
import type {AsyncSearchAsyncSearch} from '@elastic/elasticsearch/api/types'
import {AssortmentCardStore, AssortmentLayoutContextMenuItem, DefaultCatalogContextMenuItems, GroupByGroupStore, IContextMenuItems, IHeroCardsOverrides, StoresInstanceContext, utils, VipLocalStore} from '@modules'
import {AssortmentDataMap, StoresInstance} from '@stores'
import {AssortmentOverrideSerialized} from '@stores/catalog/CatalogAssortmentsStore'
import {emptyAggregationFilter, HitsResult, IAggregationResult, ICompositeBucketResult, ICompositeResult} from '@stores/elasticsearch'
import {authStore, removeUndefinedsNested} from '@utils'
import FlipMove from 'react-flip-move'
import FlipMoveProps = FlipMove.FlipMoveProps

/// region Store

export type AssortmentCardBucket = ICompositeBucketResult & {
  item: any
  key: { [key: string]: any },
  doc_count: number
  hero?: { hero?: IAggregationResult<{ item: AsyncSearchAsyncSearch<any> }> }
  swatches?: IAggregationResult<{ item: AsyncSearchAsyncSearch<any> }>
  headerAttributes: Array<any>
}

export class AssortmentRootStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, public props: { groupBy?: GroupByGroupStore }) {
    super(stores)

    if (props.groupBy) {
      props.groupBy.assortmentRoot = this
    }

    const {storeId} = this
    stores.catalog.componentStores.assortmentRoots[storeId] = this
  }

  @computed get storeId() {
    const {group} = this
    return group?.props?.info?.key ?? 'catalog'
  }

  get group() { return this.props.groupBy}

  @observable.shallow cardStoresDict: { [id: string]: AssortmentCardStore } = {}
  @observable remoteLookups: { [esIndex: string]: { [fieldEqualsValue: string]: any } } = {}

  @computed get cardStores(): Array<AssortmentCardStore> { return _.values(this.cardStoresDict)}

  @computed get isExpandedAll() {
    const {cardStores} = this
    return cardStores.every(s => s.expanded)
  }

  @computed get isCollapsedAll() {
    const {cardStores} = this

    return cardStores.every(s => !s.expanded)
  }

  @action collapseAll = () => {
    this.cardStores.forEach(cs => cs.expanded = false)
  }

  @action expandAll = () => {
    this.cardStores.forEach(cs => cs.expanded = true)
  }

  @computed get contextMenuItems() {
    return <StoresInstanceContext.Provider value={this.stores}>
      <AssortmentLayoutContextMenuItem/>
      <DefaultCatalogContextMenuItems/>
    </StoresInstanceContext.Provider>
  }

  registerEffects = () => {
    const {catalog, catalog: {assortments: {selectedAssortment}}} = this

    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        if (selectedAssortment && catalog.hasFinishedFirstLoad) {
          const {es: {catalogItemFilter}, refetchId, _sourceDynamic} = catalog,
            {group, _debounceNeedsResetRunQuery} = this, clone = _.cloneDeep(catalogItemFilter)

          runInAction(() => {
            if (!this.hasLoaded) {
              this._needsResetRunQuery = true
            } else {
              _debounceNeedsResetRunQuery()
            }
          })
        }
      }, {name: `Run query for assortment board - ${selectedAssortment?._id}}`}),

      autorun(() => {
        if (this._needsResetRunQuery && !this.loading) {
          this._resetRunQueryWhenAvailable()
        }
      }),
      autorun(() => {
        if (selectedAssortment && catalog.hasFinishedFirstLoad) {
          const {maxToLoadIndex, cardsByIndex} = this
          while (cardsByIndex.size < maxToLoadIndex && this.hasMore && !this.loading) {
            this.runQuery()
          }
        }
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  private _debounceNeedsResetRunQuery = _.debounce(() => {
    this._needsResetRunQuery = true
  }, 200)

  get assortment() { return this.catalog.assortments.selectedAssortment}

  @computed get dataMap(): AssortmentDataMap {
    return _.merge({image: 'images'} as Partial<AssortmentDataMap>, this.assortment?.dataMap)
  }

  @computed get heroCatalogItemField() {
    const {catalog, assortment: {heroCards}} = this
    if (!heroCards?.field) return null
    return catalog.findFieldInTree(heroCards.field)
  }

  @computed get heroCardsDataMap() {
    return _.merge({image: 'images', title: {field: 'name'}} as Partial<AssortmentDataMap>, this.assortment?.heroCards?.dataMap)
  }

  @computed get childCardsSource() {
    const {assortment: {heroCards}} = this
    if (_.isEmpty(heroCards)) return []

    const {child} = heroCards

    return (!child ? [] : [
      child.field || '*',
      ...(child?.attributes ?? []).map(f => _.isString(f) ? `${child.field ? `${child.field}.` : ''}${f}` : null),
      child?.bom?.field,
      child?.bom?.onlyIfField
    ])
  }
  @computed get heroCardsSource() {
    const {assortment: {heroCards}, heroCardsDataMap, childCardsSource} = this
    if (_.isEmpty(heroCards)) return []

    const {child} = heroCards
    return _.uniq(
      [
        heroCards.field,
        heroCards.orderBy?.field,
        heroCards.item?.field,
        ..._.values(heroCardsDataMap).map((v: any) => v ? (v.field ?? v) : null ? v.field : v),
        ...childCardsSource
      ].filter(_.identity)
    )
  }

  /**
   * Given the datamap and other assortment config, generate the _source list needed for the catalog items we fetch
   */
  @computed get catalogItemSource() {
    const {dataMap, filter, catalog, assortment, assortment: {grouping}, heroCatalogItemField, heroCardsSource, childCardsSource} = this
    return _.uniq(
      [
        ..._.flatMap(
        _.flatMap(
          [..._.values(dataMap), ...grouping].map(f => {
            let r = _.isArray(f) ? f.map(f => _.isString(f) ? f : f.field) : _.isString(f) ? [f] : [f.field]

            return r.map(v => [v, filter.filterFieldFor(catalog.findFieldInTree(v))])
          }))),
        ...(heroCatalogItemField
            ? (!assortment?.heroCards?.item?.field ? childCardsSource : [])
            : heroCardsSource)

    ]).filter(_.identity)
  }

  @computed get headerAttributesSource() {
    const {dataMap} = this
    return (dataMap.attributes ?? []).map(v => v.field)
  }

  /**
   * Top-level
   */
  @computed get queryVariables() {
    const {
        heroCatalogItemField, catalogItemSource, headerAttributesSource, after_key,
        perspectives, heroCardsSource, catalog, virtualPageSize, group,
        stores: {ui: {settings: {assortments: {maxSwatches}}}},
        catalog: {
          findFieldInTree, settings: {assortment: {orders}},
          queryVariablesEs, esFieldFor_byField
        }, assortment: {grouping, heroCards, swatches}
      } = this,
      groupFields = grouping.map(f => esFieldFor_byField(f, true))

    let r = removeUndefinedsNested(
      _.cloneDeep({
        ...queryVariablesEs,
        size: 0,
        aggs: {
          count: {
            cardinality: {
              script: groupFields.map(f => `doc['${f}']`).join(` + '#' + `)
            }
          },
          composite: {
            composite: {
              after: after_key,
              size: virtualPageSize,
              sources: grouping.map(f => {
                const field = esFieldFor_byField(f, true)

                return ({
                  [field]: {
                    terms: {
                      field,
                      missing_bucket: true,
                      order: orders[f] ?? 'asc'
                    }
                  }
                })
              })
            },
            aggs: {
              item: {
                top_hits: {
                  size: 1,
                  _source: catalogItemSource
                }
              },
              headerAttributes: {
                top_hits: {
                  size: 10,
                  _source: headerAttributesSource
                }
              },

              ...perspectives.reduce((acc, v) => {
                const field = catalog._fieldByName[v.field]

                return ({
                  ...acc,
                  [v.field]: {cardinality: {field: field.esIndex ? `${field.field}.id` : field.field}}
                })
              }, {}),

              // Hero card data
              ...(!heroCatalogItemField ? {} : {
                hero: {
                  filter: heroCards.esFilter ?? {bool: {}},
                  aggs: {
                    hero: {
                      terms: {
                        field: heroCards.field,
                        size: maxSwatches
                      },

                      aggs: {
                        items: {
                          top_hits: {
                            _source: heroCardsSource,
                            size: maxSwatches
                          }
                        }
                      }
                    }
                  }
                }
              }),

              // Swatches
              ...(_.isEmpty(swatches)
                  ? {}
                  : {
                  swatches: {
                    ...emptyAggregationFilter,
                    aggs: swatches.map(({field: f, dataMap}) => ({f, field: findFieldInTree(f, true)})).filter(_.identity).reduce((acc, {f, field}) => ({
                      ...acc,
                      [f]: {terms: {field: f, size: maxSwatches}, aggs: {item: {top_hits: {size: 1, _source: findFieldInTree(f, true).field}}}}
                    }), {})
                  }
                })
            }
          },
          itemCount: {
            value_count: {
              field: '_id'
            }
          },
          unassigned: {
            missing: {
              field: _.first(grouping)
            }
          }
        }
      }))

    if (group) {
      const {mustTerms, mustNotTerms} = group
      const {query: {bool}} = r
      if (!_.isEmpty(mustTerms)) {
        if (!bool.filter) { bool.filter = []}
        bool.filter.push(...mustTerms)
      }

      if (!_.isEmpty(mustNotTerms)) {
        if (!bool.must_not) { bool.must_not = []}
        bool.must_not.push(...mustNotTerms)
      }
    }

    // YFTS-9700 Remove selection filter (freezeIDs) from our query
    if (!_.isEmpty(r.query?.bool?.filter)) {
      r.query.bool.filter = r.query.bool.filter.filter(t => !_.has(t, 'terms.exId'))
    }

    const {assortment: {filter}, stores} = this
    if (filter) {
      let terms = filter.map(f => {
        return _.isFunction(f) ? f(stores) : f
      }).filter(_.identity)

      if (!_.isEmpty(terms)) {
        r.query.bool.filter.push(...terms)
      }
    }
    return r
  }

  @observable loading = false
  @observable hasLoaded = false

  /**
   * For composite query paging
   */
  @observable after_key = undefined

  /**
   * Page of composite query to result hits
   */
  cardsByIndex = observable.map<number, AssortmentCardBucket>({}, {deep: false})

  // @computed get expandedCards() {
  //   const {cardsByIndex, cardStores} = this
  //   //return Array.from(cardsByIndex.values()).filter(c => c.)
  // }

  @computed get virtualPageSize() { return this.stores.ui.settings.assortments.itemsPerQuery}

  get settings() { return this.catalog.settings.assortment}

  @computed get styles() {
    const {catalog: {imageBackgroundStyle, styleCssVariables, settings: {assortment: {layout: {rootFontSize, assortmentPadding, heroesPerAssortment, heroGap, secondLevelHeight, swatchesPerColumn, columns, columnWidth, heroHeight}}}}} = this

    return {
      styleCssVariables,
      '--catalog-image-background': imageBackgroundStyle,
      '--assortment-root-font-size': `${rootFontSize}px`,
      '--assortment-columns': columns,
      '--swatches-per-column': swatchesPerColumn,
      '--assortment-card-padding': `${assortmentPadding}em`,
      '--hero-card-gap': `${heroGap}em`,
      '--heroes-per-assortment': heroesPerAssortment,
      '--assortment-column-width': `${columnWidth}em`,
      '--hero-card-height-regular': `${heroHeight}em`,
      '--2nd-level-hero-image-height': `${secondLevelHeight}em`
    } as any
  }


  @action onContextMenu = e => this.contextMenu.onComponentContextMenu(e, this)

  @observable private _needsResetRunQuery = false

  @action private _resetRunQueryWhenAvailable = () => {
    // console.log(`Reset assortment root query`)
    if (this.loading) {
      this._needsResetRunQuery = true
    } else {
      this.serverOverrides = {}
      this.after_key = undefined
      this.maxToLoadIndex = 1
      this._needsResetRunQuery = false
      this.runQuery()
    }
  }

  @observable serverOverrides : Record<string, IHeroCardsOverrides> = {}

  @action runQuery = async () => {
    const {es, queryVariables, catalog, stores, catalog: {workspace}, serverOverrides} = this

    if (this.loading || !authStore.userIsAuthenticated) return

    this.loading = true

    try {
      const {
        aggregations: {
          composite, count, itemCount, unassigned
        }
      } = await es.search({
        body: queryVariables,
        index: catalog.index,
        meta: `Assortment query for '${stores._id}'`
      }, {log: {query: false, result: false}})

      // ignore stale results
      if (!this._needsResetRunQuery) {
        // Lookup any accompanying overrides
        const {items: assortmentOverrides} = es.unwrapHitsResult<{ overridesJson: string }>(
          await es.search({
            index: es.indexes.assortmentOverrides,
            body: {
              _source: ['overridesJson'],
              query: {
                terms: {
                  'assortmentKey.keyword': composite.buckets.map(v => stringify({...v.key, workspace: workspace._id}))
                }
              }
            },
            meta: `Lookup overrides for assortments`
          }, {log: {query: false}}))


        runInAction(() => {
          assortmentOverrides.forEach(({_id, overridesJson}) => {
            serverOverrides[_id] = JSON.parse(overridesJson)
          })
          // console.log({composite, count, itemCount, unassigned})
          const c = composite as ICompositeResult,
            {cardsByIndex} = this

          if (!this.after_key) {
            cardsByIndex.clear()
          }

          c.buckets.forEach(({key, doc_count, item, headerAttributes, ...rest}) => {
            const hit = _.first((item?.hits as HitsResult)?.hits),
              headerHits = (headerAttributes?.hits as HitsResult)?.hits ?? []

            cardsByIndex.set(cardsByIndex.size, {
              key,
              doc_count,
              item: hit ? {...hit._source, _id: hit._id} : null,
              headerAttributes: headerHits.map(({_source, _id}) => ({..._source, _id})),
              ...rest
            })
          })

          this.hasLoaded = true
          this.assortmentCount = count.value
          this.unassignedCount = unassigned.value
          this.itemCount = itemCount.value

          this.after_key = c.after_key
          this.queryTimestamp = Date.now()
        })
        await this.getItemsByIds(composite.buckets)
      }
    } finally {
      this.loading = false
    }
  }

  @observable queryTimestamp = 0

  getItemsByIds = async (buckets) => {
    const {catalog, es, heroConfig: {child}} = this

    if (child?.bom?.field && buckets.length) {
      const {bom} = child
      const values = _.flatMap(buckets, 'hero.hero.buckets'),
        cardsData = values.reduce((acc, {items}) => {
          const hits = es.unwrapHitsResult(items)
          return [
            ...acc,
            ...hits.items
          ]
        }, []),
        bomItems = _.flatMap(cardsData, bom?.field),
        ids = bomItems.map(({id}) => id)

      if (!bom.onlyIfField || _.get(_.first(cardsData), bom.onlyIfField)) {
        const idsToLoad = _.uniq(ids).filter(_.identity)
        !_.isEmpty(idsToLoad) && await es.loadItemsByIds({ids: idsToLoad, index: catalog.index})
      }
    }
  }

  get heroConfig() { return this.assortment?.heroCards}

  @computed get hero_catalogFieldSkeleton() {
    const {catalog: {_fieldByName}, heroConfig: {field}} = this
    return _fieldByName[field]
  }

  @computed get hero_foreignKeyFieldSkeleton() {
    const {catalog, hero_catalogFieldSkeleton} = this
    return hero_catalogFieldSkeleton
           ? (catalog.findFieldInTree(hero_catalogFieldSkeleton.field, true) ?? hero_catalogFieldSkeleton )
           : null
  }

  @computed get hero_foreignKeyFieldRealized() {
    const {catalog: {_fieldByName}, heroConfig: {dataMap}} = this
    const {catalog} = this
    return dataMap?.images ? catalog.findFieldInTree(dataMap?.images as string, true) : null
  }

  @computed get perspectives() {
    const {catalog: {_fieldByName}, assortment} = this
    return (assortment.perspectives ?? []).map(f => _fieldByName[f]).filter(_.identity)
    // const {hero_foreignKeyFieldSkeleton, hero_foreignKeyFieldRealized, assortment: { swatches}, catalog: {_fieldByName, findFieldInTree}} = this
    // return _.uniq(
    //   _.flatMap([
    //     this.hero_catalogFieldSkeleton, // We want the shape master not the shape (the fk field...not sure how to distinguish this well in config)
    //     hero_foreignKeyFieldRealized,
    //     _.uniq((swatches ?? []).map(s => s?.field).filter(_.identity)).map(f => findFieldInTree(f, true))
    //   ]).filter(_.identity))
  }

  /**
   * Due to limitations in the sync process, we are only given a remote supplier ID for our image, instead of the images itself
   * @param field
   * @param id
   * @param esIndex
   */
  @action lookupRemoteObject = async ({field, id, esIndex}: { field: string; id: string; esIndex: string }) => {
    const {es, remoteLookups} = this
    if (!remoteLookups[esIndex]) {
      remoteLookups[esIndex] = {}
    }

    if (!remoteLookups[esIndex][id]) {
      remoteLookups[esIndex][id] = []

      const r = await es.get({
        index: esIndex,
        meta: `AssortmentRoot - lookup remote field not included in original search results: ${field} -${id} - ${esIndex}`,
        id
      })

      // console.log({r, esIndex, id, field})
      if (r) {
        const v = _.get(r, field)
        remoteLookups[esIndex][id] = v
      }
    }
  }

  @action getHeaderImageForeignKeyValue = (data) => {
    const {es, assortment} = this
    if (!data || !assortment) return null

    const {dataMap: {images}} = assortment

    if (!images || _.isString(images)) {
      return null
    } else if (images.esIndex && images.foreignField) {
      const {field, foreignField, esIndex} = images,
        foreignKeyValueRaw = es.readField(data, images.field)?.val

      if (_.isEmpty(foreignKeyValueRaw)) return null
      return _.isArray(foreignKeyValueRaw) ? _.first(foreignKeyValueRaw) : foreignKeyValueRaw
    }
  }

  isAssortmentLoaded = index => this.cardsByIndex.has(index)

  @observable maxToLoadIndex = 1
  loadMoreAssortments = async (startIndex, stopIndex) => {
    console.log(`loadMoreItems`, {startIndex, stopIndex})
    this.maxToLoadIndex = Math.max(stopIndex, this.maxToLoadIndex)
    this.runQuery()
  }

  @observable assortmentCount = 0
  @observable unassignedCount = 0
  @observable itemCount = 0

  itemSizeForAssortment = index => 50

  @computed get showLoader() {
    const {loading, hasLoaded, cardsByIndex} = this
    return !hasLoaded || (loading && cardsByIndex.size === 0)
  }

  @computed get assortmentCardWidth() {
    const {catalog: {settings: {assortment: {layout: {rootFontSize, assortmentPadding, columnWidth, columns, heroGap}}}}} = this
    return rootFontSize * (assortmentPadding * 2 + columns * columnWidth + heroGap * (columns - 1) + heroGap)
  }

  @computed get hasMore() {
    return this.after_key != null
  }

  @action loadMoreAssortmentsAuto = () => {
    this.runQuery()
  }

  @action copyQueryToClipboard = () => {
    const {catalog, after_key, queryVariables} = this
    navigator.clipboard.writeText(`GET ${catalog.index}/_search\n${JSON.stringify(queryVariables, null, 2)}`)
  }

  @observable animations : {[key: string]: Partial<FlipMoveProps>} = {
    rootCards: {staggerDelayBy: 20, staggerDurationBy: 0, duration: 500, appearAnimation: 'fade', enterAnimation: 'fade', leaveAnimation: 'none'},
    assortmentCard: {leaveAnimation: 'fade', enterAnimation: 'none', appearAnimation: 'fade'},
    heroCards: {staggerDelayBy: 20, duration: 500, staggerDurationBy: 0, appearAnimation: 'fade', leaveAnimation: 'fade', enterAnimation: 'none'},
  }

  @computed get canExpandCards() { return !this.settings.columnView}
}

export const AssortmentRootStoreContext = React.createContext<AssortmentRootStore>(null)
export const useAssortmentRootStore = () => React.useContext(AssortmentRootStoreContext)

/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentRootStore.tsx